import { useTranslation } from 'react-i18next';
import { Button, Col, Row } from 'antd';

import { useViewClaimList, type ViewClaimFilters } from 'all-in-one-shared-api';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { useCachedClaimProperties } from 'hooks';
import { ClaimStatusDropdown, ClaimTypeDropdown, FamilyMemberDropdown, TreatmentPeriodDropdown } from './components';
import styled from 'styled-components';
import { DownloadOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import JSONCrush from 'jsoncrush';
import { useNavigate } from 'react-router-dom';

interface FilterSectionProps {
  claimsQuery: ViewClaimFilters;
  setClaimQuery: React.Dispatch<React.SetStateAction<ViewClaimFilters>>;
  token: string;
  totalCount: number;
}

const FilterRow = styled(Row)`
  margin-bottom: 1rem;
`;

const FilterSection = ({ claimsQuery, setClaimQuery, token, totalCount }: FilterSectionProps) => {
  const { t } = useTranslation();
  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);
  const [isDownloading, setIsDownloading] = useState(false);
  const navigate = useNavigate();

  const defaultMember = useMemo(
    () => claimProperties?.familyMembers?.map((a) => a.memberType).join(','),
    [claimProperties],
  );
  useEffect(() => {
    console.log(defaultMember, 'FilterSection的useEffect');
  }, [defaultMember]);
  const { shouldFetch: shouldFetchClaimHistory, data: claimHistoryData } = useViewClaimList(
    'getClaimHistory', // Note(Viet Anh): using static string because it should fetch only if clicking the button, not when the filters are changed
    token,
    {
      ...claimsQuery,
      claimStatuses: ['COMPLETED', 'DECLINED', 'NEED_ACTION'],
    },
  );

  const triggerDownloadClaimHistory = () => {
    setIsDownloading(true);
    shouldFetchClaimHistory(true);
  };

  useEffect(() => {
    if (!claimHistoryData) {
      return;
    }

    const { request, results } = claimHistoryData;
    const trimmedResults =
      results?.length > 0
        ? results.map(
            ({
              patientName,
              claimId,
              treatmentDate,
              benefit,
              claimType,
              receiptAmount,
              paidAmount,
              shortfallAmount,
              reasons,
              paidDate,
              claimStatus,
              paymentMethod,
              paymentMethodChinese,
              networkClaim,
            }) => ({
              patientName,
              claimId,
              treatmentDate,
              benefit,
              claimType,
              receiptAmount,
              paidAmount,
              shortfallAmount,
              reasons,
              paidDate,
              claimStatus,
              paymentMethod,
              paymentMethodChinese,
              networkClaim,
            }),
          )
        : [];

    const patient = claimProperties?.familyMembers?.find((member) =>
      claimsQuery.patientMemberTypes?.includes(member.memberType || ''),
    );

    const encodedData = encodeURIComponent(
      JSONCrush.crush(
        JSON.stringify({
          ...request,
          totalCount,
          patientName: `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`,
          data: trimmedResults,
        }),
      ),
    );

    setIsDownloading(false);
    /**
     * Spec:
     *  1. Once user click the magic link, it will land to another page
     *  2. User can also copy the magic link and share with others
     * That's why I don't use navigate() with state
     */
    navigate(`/claims/history?data=${encodedData}`, { state: { claimsQuery } });
  }, [
    claimHistoryData,
    navigate,
    totalCount,
    claimProperties?.familyMembers,
    claimsQuery.patientMemberTypes,
    claimsQuery,
  ]);

  return (
    <FilterRow gutter={16}>
      <Col>
        <FamilyMemberDropdown
          defaultMember={defaultMember}
          onChange={setClaimQuery}
          options={claimProperties?.familyMembers ?? []}
        />
      </Col>
      <Col>
        <ClaimStatusDropdown onChange={setClaimQuery} />
      </Col>
      <Col>
        <ClaimTypeDropdown selectedPatientMemberTypes={claimsQuery.patientMemberTypes ?? []} onChange={setClaimQuery} />
      </Col>
      <Col>
        <TreatmentPeriodDropdown onChange={setClaimQuery} />
      </Col>
      <Col>
        {/* Note(Heryani): separate into two button components based on isDownloading value,
         instead of passing isDownloading to loading prop 
         in order to resolve button state UI issue (cannot show loading icon again after first click) */}
        {isDownloading && (
          <Button icon={<DownloadOutlined />} loading={true}>
            {t('claimHistory.download')}
          </Button>
        )}
        {!isDownloading && (
          <Button icon={<DownloadOutlined />} onClick={triggerDownloadClaimHistory}>
            {t('claimHistory.download')}
          </Button>
        )}
      </Col>
    </FilterRow>
  );
};

export default FilterSection;
